import { css } from "@emotion/react";
import { BOTTOM_SHEET_HEIGHT, MAX_Y } from "@src/components/aiFilter/Constants";
import React, { useEffect, useMemo } from "react";
import { useFavoriteRegionStore } from "../store/favoriteRegionStore";
import { useFavoriteRegionItemList } from "../service/api";
import FavoriteRegionItemList from "./FavoriteRegionItemList";
import FavoriteRegionFooter from "./FavoriteRegionFooter";
import { useMajorCategory } from "@src/pages/mapFilter/useFilterList";

// const SORT_OPTIONS = [
//     { value: 0, label: "업데이트순" },
//     { value: 1, label: "감정가 높은순" },
//     { value: 2, label: "감정가 낮은순" },
//     { value: 3, label: "최저가 높은순" },
//     { value: 4, label: "최저가 낮은순" },
//     { value: 5, label: "매각기일 가까운순" },
//     { value: 7, label: "유찰회수 많은순" },
//     { value: 8, label: "유찰회수 적은순" },
// ];
// const SORT_LABEL_MAP: Record<number, string> = Object.fromEntries(SORT_OPTIONS.map((o) => [o.value, o.label]));

interface Props {
    sheetRef: React.RefObject<HTMLDivElement | null>;
    contentRef: React.RefObject<HTMLDivElement | null>;
    buttonWrapperRef: React.RefObject<HTMLDivElement | null>;
    sortRef: React.RefObject<HTMLDivElement | null>;
    buttonRef1: React.RefObject<HTMLDivElement | null>;
    buttonRef2: React.RefObject<HTMLDivElement | null>;
    buttonRef3: React.RefObject<HTMLDivElement | null>;
    hideSheet?: boolean;
    step: number;
    listVisible: boolean;
    checkShow: boolean;
    showSortBox?: boolean;
    onChangeShowSortBox?: () => void;
    onScrollUp: () => void;
    onScrollDown: () => void;
    setStep: (n: number) => void;
    onStep2Next: () => void;
    isSaving?: boolean;
    showStepFooter?: boolean;
    onClickReset?: () => void;
    isEditingCondition?: boolean;
}

const BottomSheet = ({
    sheetRef,
    contentRef,
    buttonWrapperRef,
    sortRef,
    buttonRef1,
    buttonRef2,
    buttonRef3,
    hideSheet = false,
    step,
    listVisible,
    checkShow,
    showSortBox = false,
    onChangeShowSortBox,
    onScrollUp,
    onScrollDown,
    setStep,
    onStep2Next,
    isSaving = false,
    showStepFooter = true,
    onClickReset,
    isEditingCondition = false,
}: Props) => {
    const filters = useFavoriteRegionStore((s) => s.filters);
    const checkedFilter = useFavoriteRegionStore((s) => s.checkedFilter);
    const sortType = useFavoriteRegionStore((s) => s.sortType);
    const currentFilter = filters[checkedFilter - 1] ?? { regions: [], categories: [], floor: null };
    const categoriesForApi = useMemo(() => {
        const first = currentFilter.categories?.[0];
        if (!first) return [];
        const major = useMajorCategory.find((m) => m.value === first);
        return major?.inputValue?.length ? major.inputValue : currentFilter.categories;
    }, [currentFilter.categories]);
    const listParams = useMemo(
        () => ({
            regions: currentFilter.regions.length ? currentFilter.regions.join(",") : "",
            categories: categoriesForApi,
            floor: currentFilter.floor ?? null,
            settingOrder: checkedFilter,
            sortType,
        }),
        [checkedFilter, currentFilter.regions, categoriesForApi, currentFilter.floor, sortType],
    );
    const { data, isFetching } = useFavoriteRegionItemList(listParams, listVisible);
    const effectiveData = isEditingCondition ? undefined : data;
    const effectiveTotalCount = isEditingCondition ? 0 : data?.totalCount ?? 0;

    const onClickPrev = () => {
        if (listVisible) {
            onScrollDown();
        } else {
            setStep(step - 1);
        }
    };

    const onClickNext = () => {
        if (step === 1) {
            setStep(2);
        } else if (step === 2) {
            onStep2Next();
        }
    };

    const onClickShowList = () => {
        onScrollUp();
    };

    const isJejuCortar = (cortar: string) => cortar.startsWith("50");
    const hasSigunguOrBelow = (cortar: string) => cortar.slice(2) !== "00000000";
    const hasEmdForJeju = (cortar: string) => cortar.slice(5) !== "00000";
    const canGoNextStep1 =
        currentFilter.regions.length > 0 &&
        currentFilter.regions.every((cortar) => {
            if (!hasSigunguOrBelow(cortar)) return false;
            return isJejuCortar(cortar) ? hasEmdForJeju(cortar) : true;
        });
    const canGoNextStep2 = currentFilter.categories.length > 0;

    const isPrev = showStepFooter && step === 2;
    const isNext = showStepFooter && !listVisible && step <= 2;
    const isShowList = listVisible;
    const canGoNext = step === 1 ? canGoNextStep1 : step === 2 ? canGoNextStep2 : true;
    const isReset = true;

    return (
        <>
            <div
                css={[rootBoxStyle(step, listVisible), hideSheet && hiddenSheetStyle]}
                ref={sheetRef as React.RefObject<HTMLDivElement>}
            >
                {/* useAiBottomSheet 훅이 참조하는 ref들 - 미설정 시 touch 핸들러에서 undefined 에러 방지 */}
                <div ref={buttonWrapperRef as React.RefObject<HTMLDivElement>} css={hiddenRefPlaceholder} aria-hidden>
                    <div ref={buttonRef1 as React.RefObject<HTMLDivElement>} />
                    <div ref={buttonRef2 as React.RefObject<HTMLDivElement>} />
                    <div ref={buttonRef3 as React.RefObject<HTMLDivElement>} />
                </div>
                <div ref={sortRef as React.RefObject<HTMLDivElement>} css={hiddenRefPlaceholder} aria-hidden />
                <div css={topHandlerStyle}>
                    <div />
                </div>
                <div css={filterBarStyle}>
                    <div css={totalCountStyle}>
                        총 매물<span>{effectiveTotalCount ?? 0}</span>건
                    </div>
                    {/* <div
                        ref={sortRef as React.RefObject<HTMLDivElement>}
                        role="button"
                        tabIndex={0}
                        css={sortBoxStyle}
                        onClick={() => onChangeShowSortBox?.()}
                        onKeyDown={(e) => e.key === "Enter" && onChangeShowSortBox?.()}
                    >
                        {SORT_LABEL_MAP[sortType] || "정렬"}
                        <span css={sortArrowStyle} />
                    </div> */}
                </div>
                {/* {showSortBox && (
                    <>
                        <div css={sortDimStyle} aria-hidden onClick={onChangeShowSortBox} />
                        <div css={sortDropdownStyle}>
                            {SORT_OPTIONS.map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    css={sortOptionStyle(sortType === value)}
                                    onClick={() => {
                                        setSortType(value);
                                        onChangeShowSortBox?.();
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </>
                )} */}
                <FavoriteRegionItemList contentRef={contentRef} data={effectiveData ?? undefined} isFetching={isFetching} />
            </div>
            {showStepFooter && !hideSheet && (
                <FavoriteRegionFooter
                    step={step}
                    isPrev={isPrev}
                    isNext={isNext}
                    isShowList={isShowList}
                    canGoNext={canGoNext}
                    isSaving={isSaving}
                    isReset={isReset}
                    onClickReset={onClickReset}
                    onClickPrev={onClickPrev}
                    onClickNext={onClickNext}
                    onClickShowList={onClickShowList}
                    style={{
                        opacity: checkShow && !listVisible ? "0" : "100",
                        zIndex: 999,
                        width: checkShow && !listVisible ? "0" : "100vw",
                        padding: checkShow && !listVisible ? "0" : "14px 12px",
                        height: checkShow && !listVisible ? "0" : "auto",
                        borderTop: checkShow && !listVisible ? "none" : "6px solid #f0f0f0",
                    }}
                />
            )}
        </>
    );
};

export default BottomSheet;

const hiddenSheetStyle = css`
    visibility: hidden;
    height: 0;
    overflow: hidden;
    pointer-events: none;
    box-shadow: none;
`;

const rootBoxStyle = (step: number, listVisible: boolean) => css`
    display: flex;
    flex-direction: column;
    position: fixed;
    z-index: 994;
    left: 0;
    right: 0;
    top: ${MAX_Y - 90}px;
    border-radius: 14px 14px 0 0;
    background: #fff;
    box-shadow: 0px -1px 4px rgba(0, 0, 0, 0.08);
    height: ${BOTTOM_SHEET_HEIGHT + 90}px;
    transition: transform 150ms ease-out;
    width: 100vw;
`;

const topHandlerStyle = css`
    height: 24px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    position: relative;
    padding-top: 16px;
    padding-bottom: 4px;
    display: flex;
    justify-content: center;
    div {
        width: 32px;
        height: 4px;
        border-radius: 2px;
        background: #d0d0d0;
        margin: auto;
    }
`;

const filterBarStyle = css`
    padding: 10px 14px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f0f0f0;
`;

const totalCountStyle = css`
    font-size: 14px;
    font-weight: 500;
    color: #0c002c;
    & > span {
        margin-left: 4px;
        color: #2d54df;
        font-weight: 600;
    }
`;

const loadingCountStyle = css`
    color: #666;
    font-weight: 500;
`;

// const sortBoxStyle = css`
//     display: flex;
//     align-items: center;
//     font-size: 14px;
//     color: #0c002c;
//     cursor: pointer;
// `;

// const sortArrowStyle = css`
//     margin-left: 6px;
//     width: 12px;
//     height: 12px;
//     background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230c002c' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")
//         no-repeat center;
//     background-size: contain;
// `;

// const sortDimStyle = css`
//     position: fixed;
//     top: 0;
//     left: 0;
//     right: 0;
//     bottom: 0;
//     background: rgba(0, 0, 0, 0.3);
//     z-index: 998;
// `;

// const sortDropdownStyle = css`
//     position: absolute;
//     top: 100%;
//     right: 14px;
//     margin-top: 4px;
//     background: #fff;
//     border-radius: 12px;
//     box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
//     padding: 8px 0;
//     min-width: 160px;
//     z-index: 999;
// `;

// const sortOptionStyle = (active: boolean) => css`
//     display: block;
//     width: 100%;
//     padding: 10px 16px;
//     text-align: left;
//     font-size: 14px;
//     border: none;
//     background: ${active ? "#f0f4ff" : "transparent"};
//     color: ${active ? "#2d54df" : "#0c002c"};
//     cursor: pointer;
// `;

const hiddenRefPlaceholder = css`
    position: absolute;
    left: -9999px;
    width: 0;
    height: 0;
    overflow: hidden;
    pointer-events: none;
`;
